<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-3">
            <h4 class="mb-0">Request Trace</h4>
        </div>

        <div class="alert alert-secondary">
            <div class="fw-bold mb-1">What this answers</div>
            A partner says they sent us data we do not have. Upload the list of requests they believe they sent and this reports, per row,
            whether it reached us, what we answered, and whether it was stored.
            <div class="mt-2 small">
                Our own request log cannot answer this alone — several Public API endpoints write no log row, rows are only written once a
                request passes validation, and rejected requests leave no row at all. This reads infrastructure telemetry as well.
            </div>
        </div>

        <div class="card mb-3">
            <div class="card-body">
                <div class="row g-3 align-items-end">
                    <div class="col-md-5">
                        <label class="form-label">File</label>
                        <input ref="fileInput" type="file" class="form-control" accept=".csv,.xlsx,.xls" @change="handleFileChange" />
                        <div class="form-text">
                            Columns: <code>url</code>, <code>timestamp_utc</code>, <code>body</code>. Maximum span {{ maxWindowDays }} days.
                        </div>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Company (optional)</label>
                        <input v-model.number="companyId" type="number" class="form-control" placeholder="Company id" />
                    </div>
                    <div class="col-md-2">
                        <label class="form-label">Tolerance (s)</label>
                        <input v-model.number="toleranceSeconds" type="number" class="form-control" />
                        <div class="form-text">Clock drift allowance.</div>
                    </div>
                    <div class="col-md-2">
                        <button class="btn btn-primary w-100" :disabled="!file || isLoading" @click="handleAnalyzeClick">
                            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                            {{ isLoading ? 'Analysing' : 'Analyse' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

        <div v-if="result">
            <div v-if="result.warning" class="alert alert-warning">{{ result.warning }}</div>

            <div v-if="result.samplingActive" class="alert alert-warning">
                <strong>Telemetry sampling is active for this period.</strong>
                A missing row is not proof a request was never sent. Rows shown as
                <em>Not retained</em> were sampled away — do not report those to a partner as "you did not send it".
            </div>

            <div class="d-flex flex-wrap gap-2 mb-3">
                <span v-for="(count, verdict) in result.verdictCounts" :key="verdict" class="badge fs-6" :class="badgeClass(verdict)">
                    {{ label(verdict) }}: {{ count }}
                </span>
                <span class="badge fs-6 bg-light text-dark">Rows: {{ result.totalRows }}</span>
                <span v-if="result.unparseableRows" class="badge fs-6 bg-secondary"> Unreadable: {{ result.unparseableRows }} </span>
            </div>

            <div class="table-responsive">
                <table class="table table-sm table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Row</th>
                            <th>Verdict</th>
                            <th>Timestamp (UTC)</th>
                            <th>Path</th>
                            <th>Status</th>
                            <th>Duration</th>
                            <th>Assets</th>
                            <th>What happened</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in result.items" :key="item.rowNumber">
                            <td>{{ item.rowNumber }}</td>
                            <td>
                                <span class="badge" :class="badgeClass(item.verdict)">{{ label(item.verdict) }}</span>
                            </td>
                            <td class="text-nowrap">{{ formatDate(item.timestampUtc) }}</td>
                            <td>
                                <code class="small">{{ item.path }}</code>
                            </td>
                            <td>{{ item.statusCode || '—' }}</td>
                            <td class="text-nowrap">{{ formatDuration(item.durationMs) }}</td>
                            <td class="small">{{ item.assetIds && item.assetIds.length ? item.assetIds.join(', ') : '—' }}</td>
                            <td class="small">{{ item.explanation }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
    import requestTraceService from '@/services/requestTrace.service';

    const VERDICT_LABELS = {
        0: 'Never arrived',
        1: 'Succeeded',
        2: 'Rejected by us',
        3: 'Failed on our side',
        4: 'Succeeded but slow',
        5: 'Not retained',
        NeverArrived: 'Never arrived',
        Succeeded: 'Succeeded',
        RejectedByUs: 'Rejected by us',
        FailedOnOurSide: 'Failed on our side',
        SucceededButSlow: 'Succeeded but slow',
        NotRetained: 'Not retained'
    };

    const VERDICT_CLASSES = {
        0: 'bg-secondary',
        1: 'bg-success',
        2: 'bg-warning text-dark',
        3: 'bg-danger',
        4: 'bg-info text-dark',
        5: 'bg-light text-dark',
        NeverArrived: 'bg-secondary',
        Succeeded: 'bg-success',
        RejectedByUs: 'bg-warning text-dark',
        FailedOnOurSide: 'bg-danger',
        SucceededButSlow: 'bg-info text-dark',
        NotRetained: 'bg-light text-dark'
    };

    export default {
        name: 'RequestTrace',
        data() {
            return {
                file: null,
                companyId: null,
                toleranceSeconds: 120,
                maxWindowDays: 7,
                isLoading: false,
                errorMessage: null,
                result: null
            };
        },
        methods: {
            handleFileChange(event) {
                this.file = event.target.files && event.target.files.length ? event.target.files[0] : null;
                this.errorMessage = null;
            },
            async handleAnalyzeClick() {
                if (!this.file) return;
                this.isLoading = true;
                this.errorMessage = null;
                this.result = null;
                try {
                    this.result = await requestTraceService.analyze(this.file, this.companyId, this.toleranceSeconds);
                } catch (error) {
                    this.errorMessage = (error && (error.message || error.title)) || 'Could not analyse the file.';
                } finally {
                    this.isLoading = false;
                }
            },
            label(verdict) {
                return VERDICT_LABELS[verdict] || verdict;
            },
            badgeClass(verdict) {
                return VERDICT_CLASSES[verdict] || 'bg-secondary';
            },
            formatDate(value) {
                if (!value) return '—';
                return String(value).replace('T', ' ').replace(/\.\d+/, '').replace('Z', '');
            },
            formatDuration(ms) {
                if (ms === null || ms === undefined) return '—';
                return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
            }
        }
    };
</script>
